import * as React from 'react';
import PropTypes from 'prop-types';
import {NumberField as BaseNumberField} from '@base-ui/react/number-field';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import MuiTextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

/**
 * This component is a placeholder for FormControl to correctly set the shrink label state on SSR.
 */
function SSRInitialFilled() {
    return null;
}
SSRInitialFilled.muiName = 'Input';

function setRef(ref, node) {
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
}

function mergeRefs(...refs) {
    return node => refs.forEach(r => setRef(r, node));
}

const NumberField = React.forwardRef(function NumberField({id: idProp, label, error, size = 'medium', helperText, value, onChange, placeholder, step = 1, stepStart, min, max, disabled, readOnly, fullWidth, variant: variantProp, InputProps: inputPropsProp, emptyText = '', sx, ...other}, forwardedRef) {
    const isReadOnly = readOnly || inputPropsProp?.readOnly;
    const variant = variantProp || 'outlined';
    let id = React.useId();
    if (idProp) {
        id = idProp;
    }

    if (isReadOnly) {
        const isEmpty = value === '' || value === undefined || value === null;
        const displayValue = isEmpty ? emptyText : String(value);
        return (
            <MuiTextField
                inputRef={forwardedRef}
                id={id}
                label={label}
                hiddenLabel={!label}
                value={displayValue}
                placeholder={placeholder}
                size={size}
                fullWidth={fullWidth}
                variant={variant}
                InputProps={{readOnly: true}}
                inputProps={isEmpty && emptyText ? {'data-empty-readonly': 'true'} : undefined}
                InputLabelProps={label ? {shrink: true} : undefined}
                error={error}
                helperText={helperText}
                sx={sx}
            />
        );
    }

    // Base UI expects null for empty state, not '' or undefined
    const normalizedValue = value === '' || value === undefined ? null : value;

    const handleValueChange = (newValue, event) => {
        if (onChange) {
            onChange({
                target: {value: newValue},
                ...event
            });
        }
    };

    // Handle increment/decrement from null state. `stepStart`, when given, is the
    // value the field jumps to on the first step from empty (either direction) —
    // e.g. seeding a "meter after" field from the "before" reading. Otherwise we
    // fall back to stepping off min (increment) / max (decrement).
    const handleIncrement = e => {
        if (normalizedValue === null && onChange) {
            e.preventDefault();
            const startValue = stepStart != null ? stepStart : (min != null ? min : 0) + step;
            onChange({target: {value: startValue}});
        }
    };

    const handleDecrement = e => {
        if (normalizedValue === null && onChange) {
            e.preventDefault();
            const startValue = stepStart != null ? stepStart : (max != null ? max : 0) - step;
            onChange({target: {value: startValue}});
        }
    };

    return (
        <BaseNumberField.Root
            {...other}
            disabled={disabled}
            value={normalizedValue}
            onValueChange={handleValueChange}
            step={step}
            min={min}
            max={max}
            render={(props, state) => (
                <FormControl
                    size={size}
                    sx={sx}
                    ref={props.ref}
                    disabled={state.disabled}
                    required={state.required}
                    error={error}
                    fullWidth={fullWidth}
                    variant={variant}
                >
                    {props.children}
                </FormControl>
            )}
        >
            <SSRInitialFilled {...other} />
            {label && (
                <InputLabel
                    htmlFor={id}
                    // The stepper buttons sit in a ~31px end adornment. MUI's default
                    // label maxWidth (calc(100% - 24px)) ignores adornments, so a long
                    // truncated label slides under the buttons. Pull the resting-state
                    // maxWidth in to clear them; the shrunk label floats above the field
                    // so it keeps MUI's default width (#773).
                    sx={disabled ? undefined : {
                        maxWidth: 'calc(100% - 52px)',
                        '&.MuiInputLabel-shrink': {maxWidth: 'calc(133% - 32px)'}
                    }}
                >
                    {label}
                </InputLabel>
            )}
            <BaseNumberField.Input
                id={id}
                render={(props, state) => (
                    <OutlinedInput
                        label={label}
                        placeholder={placeholder}
                        inputRef={mergeRefs(props.ref, forwardedRef)}
                        value={state.inputValue}
                        onBlur={props.onBlur}
                        onChange={props.onChange}
                        onKeyUp={props.onKeyUp}
                        onKeyDown={props.onKeyDown}
                        onFocus={props.onFocus}
                        slotProps={{
                            input: props
                        }}
                        endAdornment={disabled ? undefined :
                            <InputAdornment
                                position="end"
                                sx={{
                                    flexDirection: 'column',
                                    maxHeight: 'unset',
                                    alignSelf: 'stretch',
                                    borderLeft: '1px solid',
                                    borderColor: 'divider',
                                    ml: 0,
                                    '& button': {
                                        py: 0,
                                        flex: 1,
                                        minWidth: 30,
                                        borderRadius: 0
                                    }
                                }}
                            >
                                <BaseNumberField.Increment
                                    render={props => (
                                        <IconButton
                                            {...props}
                                            size={size}
                                            aria-label="Increase"
                                            onClick={e => {
                                                handleIncrement(e);
                                                if (!e.defaultPrevented) {
                                                    props.onClick?.(e);
                                                }
                                            }}
                                        >
                                            <KeyboardArrowUpIcon
                                                fontSize={size}
                                                sx={{transform: 'translateY(2px)'}}
                                            />
                                        </IconButton>
                                    )}
                                />

                                <BaseNumberField.Decrement
                                    render={props => (
                                        <IconButton
                                            {...props}
                                            size={size}
                                            aria-label="Decrease"
                                            onClick={e => {
                                                handleDecrement(e);
                                                if (!e.defaultPrevented) {
                                                    props.onClick?.(e);
                                                }
                                            }}
                                        >
                                            <KeyboardArrowDownIcon
                                                fontSize={size}
                                                sx={{transform: 'translateY(-2px)'}}
                                            />
                                        </IconButton>
                                    )}
                                />
                            </InputAdornment>
                        }
                        sx={{pr: disabled ? undefined : 0}}
                    />
                )}
            />
            {helperText && <FormHelperText sx={{ml: 0, '&:empty': {mt: 0}}}>{helperText}</FormHelperText>}
        </BaseNumberField.Root>
    );
});

NumberField.propTypes = {
    error: PropTypes.bool,
    fullWidth: PropTypes.bool,
    id: PropTypes.string,
    label: PropTypes.node,
    max: PropTypes.number,
    min: PropTypes.number,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    size: PropTypes.oneOf(['medium', 'small']),
    step: PropTypes.number,
    stepStart: PropTypes.number,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default NumberField;
